import React, { useState } from 'react';
import { 
  User, Mail, Phone, Building2, Briefcase, Calendar, 
  MessageSquare, ChevronRight, ChevronLeft, Check, 
  Send, Sparkles, Globe, ShoppingCart, RefreshCw, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Vos coordonnées', icon: User },
  { id: 2, title: 'Votre projet', icon: Briefcase },
  { id: 3, title: 'Détails', icon: MessageSquare },
  { id: 4, title: 'Confirmation', icon: Check },
];

const PROJECT_TYPES = [
  { id: 'site-vitrine', label: 'Site vitrine', icon: Globe, description: 'Présentez votre activité' },
  { id: 'e-commerce', label: 'E-commerce', icon: ShoppingCart, description: 'Vendez en ligne' },
  { id: 'application-web', label: 'Application web', icon: Layers, description: 'Solution sur-mesure' },
  { id: 'refonte', label: 'Refonte de site', icon: RefreshCw, description: 'Modernisez votre site' },
  { id: 'autre', label: 'Autre projet', icon: Sparkles, description: 'Projet spécifique' },
];

const BUDGETS = [
  { id: 'small', label: '< 3 000 €', description: 'Projet simple' },
  { id: 'medium', label: '3 000 € - 10 000 €', description: 'Projet standard' },
  { id: 'large', label: '10 000 € - 30 000 €', description: 'Projet avancé' },
  { id: 'enterprise', label: '> 30 000 €', description: 'Projet complexe' },
  { id: 'unknown', label: 'À définir', description: 'Discutons-en' },
];

const TIMELINES = [
  { id: 'urgent', label: '< 1 mois', tag: 'Urgent' },
  { id: 'short', label: '1 - 2 mois', tag: 'Court' },
  { id: 'medium', label: '2 - 4 mois', tag: 'Standard' },
  { id: 'long', label: '> 4 mois', tag: 'Flexible' },
];

const MultiStepContactForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Coordonnées
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    // Step 2: Projet
    projectType: '',
    budget: '',
    timeline: '',
    // Step 3: Détails
    message: '',
    features: '',
    hasDesign: '',
    consent: false,
  });

  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'Prénom requis';
      if (!formData.lastName.trim()) newErrors.lastName = 'Nom requis';
      if (!formData.email.trim()) {
        newErrors.email = 'Email requis';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Email invalide';
      }
    }
    
    if (step === 2) {
      if (!formData.projectType) newErrors.projectType = 'Sélectionnez un type de projet';
      if (!formData.budget) newErrors.budget = 'Sélectionnez un budget';
      if (!formData.timeline) newErrors.timeline = 'Sélectionnez un délai';
    }
    
    if (step === 3) {
      if (!formData.message.trim()) {
        newErrors.message = 'Décrivez votre projet';
      } else if (formData.message.trim().length < 10) {
        newErrors.message = 'La description doit contenir au moins 10 caractères';
      }
      if (!formData.consent) newErrors.consent = 'Veuillez accepter la politique de confidentialité';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    setIsSubmitting(true);
    
    try {
      // Prepare data for API
      const apiData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone || null,
        company: formData.company || null,
        project_type: formData.projectType,
        budget: formData.budget,
        timeline: formData.timeline,
        message: formData.message,
        features: formData.features || null,
        has_design: formData.hasDesign || null
      };
      
      // Submit to backend API
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, apiData);
      
      if (response.data.success) {
        setIsComplete(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = 'Une erreur est survenue. Veuillez réessayer.';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors
        errorMessage = detail.map(e => e.msg).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProjectTypeLabel = () => {
    const type = PROJECT_TYPES.find(t => t.id === formData.projectType);
    return type ? type.label : '';
  };

  const getBudgetLabel = () => {
    const budget = BUDGETS.find(b => b.id === formData.budget);
    return budget ? budget.label : '';
  };

  const getTimelineLabel = () => {
    const timeline = TIMELINES.find(t => t.id === formData.timeline);
    return timeline ? timeline.label : '';
  };

  // Success screen
  if (isComplete) {
    return (
      <div className="bg-white/[0.02] border border-white/[0.05] p-8 lg:p-12 text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-accent/20 rounded-full flex items-center justify-center animate-pulse">
          <Check className="w-10 h-10 text-accent" />
        </div>
        <h3 className="text-2xl font-semibold text-white mb-4">
          Demande envoyée avec succès !
        </h3>
        <p className="text-white/60 mb-8 max-w-md mx-auto">
          Merci {formData.firstName} ! Notre équipe analysera votre projet et vous recontactera sous 24-48h.
        </p>
        <div className="bg-white/[0.03] border border-white/[0.05] p-6 text-left max-w-sm mx-auto">
          <h4 className="text-white/80 text-sm font-medium mb-4">Récapitulatif :</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-white/50">Projet</span>
              <span className="text-white">{getProjectTypeLabel()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/50">Budget</span>
              <span className="text-white">{getBudgetLabel()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/50">Délai</span>
              <span className="text-white">{getTimelineLabel()}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/[0.05] overflow-hidden">
      {/* Progress Header */}
      <div className="bg-white/[0.02] border-b border-white/[0.05] p-6">
        <div className="flex items-center justify-between mb-4">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div 
                  className={`w-10 h-10 flex items-center justify-center transition-all duration-500 ${
                    currentStep > step.id 
                      ? 'bg-accent text-black' 
                      : currentStep === step.id 
                        ? 'bg-accent/20 text-accent border border-accent/50' 
                        : 'bg-white/[0.05] text-white/30'
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check size={18} />
                  ) : (
                    <step.icon size={18} />
                  )}
                </div>
                <span className={`text-xs mt-2 hidden sm:block transition-colors duration-300 ${
                  currentStep >= step.id ? 'text-white/80' : 'text-white/30'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className="flex-1 mx-2 h-px bg-white/[0.1] relative">
                  <div 
                    className="absolute inset-y-0 left-0 bg-accent transition-all duration-500"
                    style={{ width: currentStep > step.id ? '100%' : '0%' }}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="text-center">
          <span className="text-accent text-sm">Étape {currentStep} sur 4</span>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-6 lg:p-8">
        {/* Step 1: Coordonnées */}
        {currentStep === 1 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <h3 className="text-xl font-semibold text-white mb-2">Vos coordonnées</h3>
              <p className="text-white/50 text-sm">Commençons par faire connaissance</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-white/70 text-sm mb-2 block">Prénom *</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => updateField('firstName', e.target.value)}
                    className={`w-full bg-white/[0.03] border ${errors.firstName ? 'border-red-500/50' : 'border-white/[0.1]'} pl-11 pr-4 py-3 text-white placeholder-white/30 focus:border-accent/50 focus:outline-none transition-colors duration-300`}
                    placeholder="Jean"
                  />
                </div>
                {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <label className="text-white/70 text-sm mb-2 block">Nom *</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    className={`w-full bg-white/[0.03] border ${errors.lastName ? 'border-red-500/50' : 'border-white/[0.1]'} pl-11 pr-4 py-3 text-white placeholder-white/30 focus:border-accent/50 focus:outline-none transition-colors duration-300`}
                    placeholder="Dupont"
                  />
                </div>
                {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
              </div>
            </div>

            <div>
              <label className="text-white/70 text-sm mb-2 block">Email *</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={`w-full bg-white/[0.03] border ${errors.email ? 'border-red-500/50' : 'border-white/[0.1]'} pl-11 pr-4 py-3 text-white placeholder-white/30 focus:border-accent/50 focus:outline-none transition-colors duration-300`}
                  placeholder="jean@exemple.com"
                />
              </div>
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-white/70 text-sm mb-2 block">Téléphone</label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    className="w-full bg-white/[0.03] border border-white/[0.1] pl-11 pr-4 py-3 text-white placeholder-white/30 focus:border-accent/50 focus:outline-none transition-colors duration-300"
                    placeholder="06 12 34 56 78"
                  />
                </div>
              </div>
              <div>
                <label className="text-white/70 text-sm mb-2 block">Entreprise</label>
                <div className="relative">
                  <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => updateField('company', e.target.value)}
                    className="w-full bg-white/[0.03] border border-white/[0.1] pl-11 pr-4 py-3 text-white placeholder-white/30 focus:border-accent/50 focus:outline-none transition-colors duration-300"
                    placeholder="Ma Société"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Projet */}
        {currentStep === 2 && (
          <div className="space-y-8 animate-fadeIn">
            <div className="text-center mb-8">
              <h3 className="text-xl font-semibold text-white mb-2">Votre projet</h3>
              <p className="text-white/50 text-sm">Aidez-nous à comprendre vos besoins</p>
            </div>

            {/* Project Type */}
            <div>
              <label className="text-white/70 text-sm mb-3 block">Type de projet *</label>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {PROJECT_TYPES.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => updateField('projectType', type.id)}
                    className={`p-4 text-left transition-all duration-300 ${
                      formData.projectType === type.id
                        ? 'bg-accent/10 border-accent/50 border-2'
                        : 'bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.15]'
                    }`}
                  >
                    <type.icon className={`w-5 h-5 mb-2 ${formData.projectType === type.id ? 'text-accent' : 'text-white/50'}`} />
                    <div className={`font-medium text-sm ${formData.projectType === type.id ? 'text-accent' : 'text-white'}`}>
                      {type.label}
                    </div>
                    <div className="text-white/40 text-xs mt-1">{type.description}</div>
                  </button>
                ))}
              </div>
              {errors.projectType && <p className="text-red-400 text-xs mt-2">{errors.projectType}</p>}
            </div>

            {/* Budget */}
            <div>
              <label className="text-white/70 text-sm mb-3 block">Budget estimé *</label>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {BUDGETS.map((budget) => (
                  <button
                    key={budget.id}
                    type="button"
                    onClick={() => updateField('budget', budget.id)}
                    className={`p-3 text-center transition-all duration-300 ${
                      formData.budget === budget.id
                        ? 'bg-accent/10 border-accent/50 border-2'
                        : 'bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.15]'
                    }`}
                  >
                    <div className={`font-medium text-sm ${formData.budget === budget.id ? 'text-accent' : 'text-white'}`}>
                      {budget.label}
                    </div>
                    <div className="text-white/40 text-xs mt-1">{budget.description}</div>
                  </button>
                ))}
              </div>
              {errors.budget && <p className="text-red-400 text-xs mt-2">{errors.budget}</p>}
            </div>

            {/* Timeline */}
            <div>
              <label className="text-white/70 text-sm mb-3 block">Délai souhaité *</label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {TIMELINES.map((timeline) => (
                  <button
                    key={timeline.id}
                    type="button"
                    onClick={() => updateField('timeline', timeline.id)}
                    className={`p-3 text-center transition-all duration-300 ${
                      formData.timeline === timeline.id
                        ? 'bg-accent/10 border-accent/50 border-2'
                        : 'bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.15]'
                    }`}
                  >
                    <div className={`font-medium text-sm ${formData.timeline === timeline.id ? 'text-accent' : 'text-white'}`}>
                      {timeline.label}
                    </div>
                    <div className={`text-xs mt-1 px-2 py-0.5 inline-block ${
                      formData.timeline === timeline.id ? 'bg-accent/20 text-accent' : 'bg-white/[0.05] text-white/40'
                    }`}>
                      {timeline.tag}
                    </div>
                  </button>
                ))}
              </div>
              {errors.timeline && <p className="text-red-400 text-xs mt-2">{errors.timeline}</p>}
            </div>
          </div>
        )}

        {/* Step 3: Détails */}
        {currentStep === 3 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <h3 className="text-xl font-semibold text-white mb-2">Détails du projet</h3>
              <p className="text-white/50 text-sm">Parlez-nous de votre vision</p>
            </div>

            <div>
              <label className="text-white/70 text-sm mb-2 block">Décrivez votre projet *</label>
              <textarea
                value={formData.message}
                onChange={(e) => updateField('message', e.target.value)}
                rows={4}
                className={`w-full bg-white/[0.03] border ${errors.message ? 'border-red-500/50' : 'border-white/[0.1]'} px-4 py-3 text-white placeholder-white/30 focus:border-accent/50 focus:outline-none transition-colors duration-300 resize-none`}
                placeholder="Décrivez votre projet, vos objectifs, votre cible..."
              />
              {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
            </div>

            <div>
              <label className="text-white/70 text-sm mb-2 block">Fonctionnalités souhaitées</label>
              <textarea
                value={formData.features}
                onChange={(e) => updateField('features', e.target.value)}
                rows={3}
                className="w-full bg-white/[0.03] border border-white/[0.1] px-4 py-3 text-white placeholder-white/30 focus:border-accent/50 focus:outline-none transition-colors duration-300 resize-none"
                placeholder="Listez les fonctionnalités importantes pour vous..."
              />
            </div>

            <div>
              <label className="text-white/70 text-sm mb-3 block">Avez-vous déjà un design ou une charte graphique ?</label>
              <div className="flex gap-3">
                {['Oui', 'Non', 'Partiellement'].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updateField('hasDesign', option)}
                    className={`px-6 py-2 text-sm transition-all duration-300 ${
                      formData.hasDesign === option
                        ? 'bg-accent text-black'
                        : 'bg-white/[0.02] border border-white/[0.1] text-white/70 hover:border-white/[0.2]'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-3 pt-4">
              <input
                type="checkbox"
                checked={formData.consent}
                onChange={(e) => updateField('consent', e.target.checked)}
                className="mt-1 w-4 h-4 accent-accent bg-transparent border-white/20"
              />
              <label className={`text-sm leading-relaxed ${errors.consent ? 'text-red-400' : 'text-white/50'}`}>
                J'accepte que mes données soient traitées conformément à la{' '}
                <a href="#" className="text-accent hover:underline">politique de confidentialité</a>. 
                Webysee s'engage à ne jamais partager vos informations avec des tiers.
              </label>
            </div>
            {errors.consent && <p className="text-red-400 text-xs">{errors.consent}</p>}
          </div>
        )}

        {/* Step 4: Confirmation */}
        {currentStep === 4 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <h3 className="text-xl font-semibold text-white mb-2">Récapitulatif</h3>
              <p className="text-white/50 text-sm">Vérifiez vos informations avant envoi</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Contact Info */}
              <div className="bg-white/[0.02] border border-white/[0.05] p-5">
                <h4 className="text-accent text-sm font-medium mb-4 flex items-center gap-2">
                  <User size={16} /> Vos coordonnées
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Nom</span>
                    <span className="text-white">{formData.firstName} {formData.lastName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Email</span>
                    <span className="text-white">{formData.email}</span>
                  </div>
                  {formData.phone && (
                    <div className="flex justify-between">
                      <span className="text-white/50">Téléphone</span>
                      <span className="text-white">{formData.phone}</span>
                    </div>
                  )}
                  {formData.company && (
                    <div className="flex justify-between">
                      <span className="text-white/50">Entreprise</span>
                      <span className="text-white">{formData.company}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Project Info */}
              <div className="bg-white/[0.02] border border-white/[0.05] p-5">
                <h4 className="text-accent text-sm font-medium mb-4 flex items-center gap-2">
                  <Briefcase size={16} /> Votre projet
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Type</span>
                    <span className="text-white">{getProjectTypeLabel()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Budget</span>
                    <span className="text-white">{getBudgetLabel()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Délai</span>
                    <span className="text-white">{getTimelineLabel()}</span>
                  </div>
                  {formData.hasDesign && (
                    <div className="flex justify-between">
                      <span className="text-white/50">Design existant</span>
                      <span className="text-white">{formData.hasDesign}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Message Preview */}
            <div className="bg-white/[0.02] border border-white/[0.05] p-5">
              <h4 className="text-accent text-sm font-medium mb-3 flex items-center gap-2">
                <MessageSquare size={16} /> Description du projet
              </h4>
              <p className="text-white/70 text-sm leading-relaxed">{formData.message}</p>
              {formData.features && (
                <>
                  <h5 className="text-white/50 text-xs mt-4 mb-2">Fonctionnalités souhaitées :</h5>
                  <p className="text-white/70 text-sm">{formData.features}</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-white/[0.05]">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={prevStep}
              className="flex items-center gap-2 px-6 py-3 bg-white/[0.05] text-white/70 hover:bg-white/[0.1] hover:text-white transition-all duration-300"
            >
              <ChevronLeft size={18} />
              Précédent
            </button>
          ) : (
            <div />
          )}

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              className="group flex items-center gap-2 px-8 py-3 bg-accent text-black font-medium hover:bg-accent/10 hover:text-accent transition-all duration-300"
            >
              Continuer
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="group flex items-center gap-2 px-8 py-3 bg-accent text-black font-medium hover:bg-accent/10 hover:text-accent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  Envoyer ma demande
                  <Send size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiStepContactForm;
